import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Repeat, X } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import type { PortalOrder, RefillIntervalDays, RefillPlan } from '../../types/refills';

/**
 * "Set up auto-refill": set up a refill plan from a received order.
 *
 * The copy carries the design decision: a plan PREPARES an order and texts a
 * payment link; it never charges a card. Consents are the same ones checkout
 * asks for, given once here and recorded on each refill order.
 */

export const REFILL_INTERVALS: RefillIntervalDays[] = [30, 60, 90];

const CONSENT_LABELS: Record<string, string> = {
  compounding: 'compounding pharmacy disclaimer',
  telehealth: 'telehealth consent',
  hipaa: 'privacy notice (HIPAA)',
  tos: 'terms of service',
  payment_processor: 'payment processing notice',
};

/** The message the API put on a refused request, or a fallback. */
export function refillApiError(error: unknown, fallback: string): { message: string; code?: string } {
  const data = (error as { response?: { data?: { error?: string; code?: string } } })?.response?.data;
  return { message: data?.error || fallback, code: data?.code };
}

export function refillDate(iso?: string | null) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

export function ConsentCheckbox({ consents, checked, onChange }: { consents: string[]; checked: boolean; onChange: (v: boolean) => void }) {
  const names = consents.map(c => CONSENT_LABELS[c] || c.replace(/_/g, ' '));
  return <label className="flex cursor-pointer items-start gap-3 rounded-2xl bg-white/70 p-3 text-sm ring-1 ring-[var(--portal-line)]">
    <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--portal-accent)]" checked={checked} onChange={e => onChange(e.target.checked)} />
    <span>I agree that the order consents I gave at checkout ({names.join(', ')}) apply to each refill.</span>
  </label>;
}

export function AutoRefillSheet({ order, requiredConsents, onClose }: {
  order: PortalOrder;
  requiredConsents: string[];
  onClose: () => void;
}) {
  const { physicianId } = useDtpParams();
  const titleId = useId();
  const queryClient = useQueryClient();
  const [interval, setIntervalDays] = useState<RefillIntervalDays>(30);
  const [agreed, setAgreed] = useState(false);
  const firstControl = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    firstControl.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const create = useMutation({
    mutationFn: async (): Promise<RefillPlan> => {
      const res = await dtpApi.createRefillPlan({ sourceOrderId: order._id as string, intervalDays: interval, consents: requiredConsents });
      if (!res.success || !res.data) throw new Error(res.error || 'Could not set up auto-refill');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refill-plans'] });
      queryClient.invalidateQueries({ queryKey: ['refill-eligibility'] });
    },
  });
  const error = create.error ? refillApiError(create.error, 'We couldn’t set up auto-refill. Please try again.').message : '';

  return <div className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(36,26,21,0.4)] sm:items-center" onClick={onClose}>
    <div role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={e => e.stopPropagation()}
      className="portal-card max-h-[92vh] w-full overflow-y-auto rounded-b-none bg-[var(--portal-card-solid)] p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:max-w-md sm:rounded-b-[inherit] sm:pb-6">
      <header className="flex items-start justify-between gap-3">
        <h2 id={titleId} className="portal-display text-2xl font-bold">{create.isSuccess ? 'Auto-refill is on' : 'Auto-refill this order'}</h2>
        <button type="button" onClick={onClose} aria-label="Close" className="grid h-10 w-10 shrink-0 place-items-center rounded-full hover:bg-white/80">
          <X aria-hidden="true" className="h-5 w-5" />
        </button>
      </header>

      {create.isSuccess ? (
        <div className="mt-4 space-y-4">
          <p className="flex items-start gap-3 text-[15px]">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#dcf2e3] text-[#14532d]"><Check aria-hidden="true" className="h-4 w-4" /></span>
            <span>We’ll prepare your first refill on <strong>{refillDate(create.data.nextRunAt)}</strong> and text you a link to pay. Nothing is charged automatically.</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <Link to={dtpPath(physicianId, '/refills')} className="inline-flex h-10 items-center rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90">Manage refills</Link>
            <button type="button" onClick={onClose} className="inline-flex h-10 items-center rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] hover:bg-white">Done</button>
          </div>
        </div>
      ) : (
        <form className="mt-4 space-y-5" onSubmit={e => { e.preventDefault(); if (agreed) create.mutate(); }}>
          <ul className="space-y-1 text-[15px]">
            {order.items?.map((item, i) => <li key={`${item.productId}-${i}`} className="flex items-center gap-2"><Repeat aria-hidden="true" className="h-4 w-4 text-[var(--portal-muted)]" />{item.productName}</li>)}
          </ul>

          <fieldset>
            <legend className="text-sm font-semibold">How often?</legend>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {REFILL_INTERVALS.map((days, i) => (
                <button key={days} ref={i === 0 ? firstControl : undefined} type="button" aria-pressed={interval === days} onClick={() => setIntervalDays(days)}
                  className={`h-11 rounded-full text-sm font-semibold ring-1 transition ${interval === days ? 'bg-[var(--portal-ink)] text-white ring-transparent' : 'bg-white/80 ring-[var(--portal-line)] hover:bg-white'}`}>
                  {days} days
                </button>
              ))}
            </div>
          </fieldset>

          <p className="rounded-2xl bg-[var(--portal-tint-2)] p-4 text-[15px]">
            We’ll prepare this order every {interval} days and text you a link to pay; nothing is charged automatically.
            <span className="mt-1 block text-sm text-[var(--portal-muted)]">Each refill uses your clinic’s price on the day it’s prepared. Pause, skip or cancel any time.</span>
          </p>

          <ConsentCheckbox consents={requiredConsents} checked={agreed} onChange={setAgreed} />

          {error && <p role="alert" className="rounded-2xl bg-[#fde4dc] p-3 text-sm text-[#8c2a14]">{error}</p>}

          <button type="submit" disabled={!agreed || create.isPending}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[var(--portal-primary)] text-sm font-semibold text-[var(--portal-primary-fg)] transition hover:opacity-90 disabled:opacity-50">
            {create.isPending && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}Turn on auto-refill
          </button>
        </form>
      )}
    </div>
  </div>;
}
